import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as FileIcons from '@/components/atoms/icons/file'
import * as MediaIcons from '@/components/atoms/icons/media'
import {
  DOCUMENT_ACCEPT_TYPES,
  IMAGE_ACCEPT_TYPES,
  VIDEO_ACCEPT_TYPES,
} from '@/shared/constants/files'

export type FileUploadVariant = 'image' | 'video' | 'document'

interface UseFileUploadProps {
  variant: FileUploadVariant
  multiple: boolean
  onFileSelect?: (file: File) => void
  onFilesSelect?: (files: File[]) => void
}

const VARIANT_CONFIG = {
  image: {
    icon: MediaIcons.Image01Icon,
    labelKey: 'Image',
    accept: IMAGE_ACCEPT_TYPES,
    className: 'w-full aspect-video md:aspect-3/1 min-h-38.5',
  },
  video: {
    icon: MediaIcons.PlayCircleIcon,
    labelKey: 'Video',
    accept: VIDEO_ACCEPT_TYPES,
    className: 'h-93 md:h-58.25 md:w-full',
  },
  document: {
    icon: FileIcons.FileDocumentIcon,
    labelKey: 'Document',
    accept: DOCUMENT_ACCEPT_TYPES,
    className: 'w-full aspect-video md:aspect-3/1 min-h-38.5',
  },
} as const

export function useFileUpload({
  variant,
  multiple,
  onFileSelect,
  onFilesSelect,
}: UseFileUploadProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)

  const config = VARIANT_CONFIG[variant]
  const VariantIcon = config.icon
  const uploadLabel = t(`common.fileUpload.upload${config.labelKey}`)
  const selectLabel = t(`common.fileUpload.select${config.labelKey}`)
  const changeLabel = t(`common.fileUpload.change${config.labelKey}`)
  const formats = t(`common.fileUpload.formats${config.labelKey}`)
  const accept = config.accept
  const variantClassName = config.className

  function emitFiles(fileList: FileList | undefined) {
    const files = Array.from(fileList ?? [])
    const [firstFile] = files
    if (!firstFile) return
    if (multiple) {
      onFilesSelect?.(files)
      return
    }
    onFileSelect?.(firstFile)
  }

  function handleClick() {
    inputRef.current?.click()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    emitFiles(e.target.files ?? undefined)
    e.target.value = ''
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    emitFiles(e.dataTransfer.files ?? undefined)
  }

  return {
    inputRef,
    isDragging,
    handleClick,
    handleChange,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    VariantIcon,
    uploadLabel,
    selectLabel,
    changeLabel,
    formats,
    accept,
    variantClassName,
  }
}
